import React, { useState, useEffect, useMemo } from 'react';
import { AppContextProvider } from '../contexts';
import { ContextualizedApp } from '../app';
import { CircularProgress, Box } from "@mui/joy";
import { Backdrop } from "@mui/material";
import { useDataProvider } from '../../mtska-frontend-data-provider';
import { useTranslation } from 'react-i18next';
import * as Modules from '../../mtska-frontend-modules';
import '../../mtska-frontend-app-component/styles/components.scss';
import { useWindowDimensions } from './../hooks/useWindowDimensions'
import {
    APP_CONFIG_CACHE_KEY,
    APP_CONFIG_TTL_MS,
    AUTH_USER_TTL_MS,
    getAuthUserCacheKey,
    USER_CONFIG_TTL_MS,
    getUserConfigCacheKey,
    hashToken,
    readCacheEntry,
    removeCache,
    writeCache,
} from '../../mtska-frontend-app-component/utils/cacheTtl';
import { store } from '../../mtska-frontend-data-provider/redux/store';

const App = () => {

    const { innerHeight, innerWidth } = useWindowDimensions();

    const { i18n } = useTranslation();

    const { loadOne } = useDataProvider('auth/token');
    const { item: config, loadOne: loadConfig } = useDataProvider('configs/config');
    const { item: appconfig, loadOne: loadAppConfig } = useDataProvider('applications/app');

    const [appConfiguration, setAppConfiguration] = useState()
    const [user, setUser] = useState()
    const [userCapabilities, setUserCapabilities] = useState()
    const [existingModules, setExistingModules] = useState({})
    const [loginError, setLoginError] = useState()
    const [loginOptions, setLoginOptions] = useState()
    const [authBootstrapStatus, setAuthBootstrapStatus] = useState('loading')

    const token = localStorage.getItem('token')

    useEffect(() => {
        const timers = [];
        let isCancelled = false;

        const scheduleRefresh = (callback, delayMs) => {
            const safeDelay = Math.max(0, delayMs);
            const timerId = window.setTimeout(callback, safeDelay);
            timers.push(timerId);
        };

        const getRemainingMs = (cachedAt, ttlMs) => ttlMs - (Date.now() - cachedAt);

        const hydrateDataProviderFromCache = (dataProviderName, data) => {
            store.dispatch({
                dataProviderName,
                uid: `cache-hydration-${dataProviderName}`,
                type: 'GETONE_SUCCESS',
                item: { data }
            });
        };

        const isValidAppConfiguration = (appConfigData) => {
            return Boolean(
                appConfigData &&
                appConfigData.modules &&
                Array.isArray(appConfigData.modules.active)
            );
        };

        const isValidUserConfiguration = (userConfigData) => {
            return Boolean(
                userConfigData &&
                typeof userConfigData === 'object' &&
                userConfigData.modules &&
                typeof userConfigData.modules === 'object'
            );
        };

        const applyAppConfiguration = (appConfigData) => {
            if (!isValidAppConfiguration(appConfigData)) return;
            const normalizedCustom = (typeof appConfigData?.custom === 'object' && appConfigData.custom !== null) ? appConfigData.custom : {};
            if (typeof normalizedCustom.menuCustomLabel !== 'object' || normalizedCustom.menuCustomLabel === null || Array.isArray(normalizedCustom.menuCustomLabel)) {
                normalizedCustom.menuCustomLabel = {};
            }
            appConfigData.custom = normalizedCustom;
            appConfigData.modules.active = appConfigData.modules.active.map((module) => Modules[module]).filter((item) => { return item !== undefined });
            setAppConfiguration(appConfigData);
        };

        const normalizeAuthErrorMessage = (error, fallbackMessage = 'User is missing required privileges to access the application') => {
            const payload = error?.response?.data ?? error;
            if (typeof payload === 'string' && payload.trim() !== '') return payload;
            if (typeof payload?.errorMessage === 'string' && payload.errorMessage.trim() !== '') return payload.errorMessage;
            if (typeof payload?.message === 'string' && payload.message.trim() !== '') return payload.message;
            return fallbackMessage;
        };

        const setUnauthorizedState = (message, options = null) => {
            if (isCancelled) return;
            setLoginError(message);
            setLoginOptions(options);
            setUser(null);
            setUserCapabilities([]);
            setExistingModules({});
            setAuthBootstrapStatus('unauthorized');
        };

        const setAuthorizedState = () => {
            if (isCancelled) return;
            setLoginError(null);
            setLoginOptions(null);
            setAuthBootstrapStatus('authorized');
        };

        const refreshAppConfiguration = () => {
            loadAppConfig('670e3907863d1b0bfa9706b9').then((response) => {
                if (response && response.item && response.item.data) {
                    writeCache(APP_CONFIG_CACHE_KEY, response.item.data);
                    applyAppConfiguration(response.item.data);
                }
            });
        };

        const appConfigCacheEntry = readCacheEntry(APP_CONFIG_CACHE_KEY);
        const appCacheIsValid = isValidAppConfiguration(appConfigCacheEntry?.value) && getRemainingMs(appConfigCacheEntry.cachedAt, APP_CONFIG_TTL_MS) > 0;
        if (appCacheIsValid) {
            applyAppConfiguration(appConfigCacheEntry.value);
            scheduleRefresh(refreshAppConfiguration, getRemainingMs(appConfigCacheEntry.cachedAt, APP_CONFIG_TTL_MS));
        } else {
            removeCache(APP_CONFIG_CACHE_KEY);
            refreshAppConfiguration();
        }

        // Intentionally disabled: avoid /applications/module/0 request.
        // loadModuleConfig('0').then((response) => {
        //     if (response && response.item && response.item.data) {
        //         setExistingModules(response.item.data);
        //     }
        // });

        if (token != null) {
            setAuthBootstrapStatus('loading');
            const authUserCacheKey = getAuthUserCacheKey(hashToken(token));
            const refreshAuthUser = () => {
                return loadOne(localStorage.getItem('token')).then((response) => {
                    const userData = response?.item?.data;
                    if (!userData) {
                        setUnauthorizedState('Unable to validate user session.');
                        return false;
                    }

                    if (userData.errorMessage) {
                        setUnauthorizedState(userData.errorMessage);
                        return false;
                    }

                    if (userData.options) {
                        setUnauthorizedState(true, userData.options);
                        return false;
                    }

                    writeCache(authUserCacheKey, userData);
                    userData.locale && i18n.changeLanguage(userData.locale);
                    if (!isCancelled) {
                        setUser(userData);
                    }
                    setAuthorizedState();
                    return true;
                }).catch((error) => {
                    setUnauthorizedState(normalizeAuthErrorMessage(error));
                    return false;
                });
            };

            const authUserCacheEntry = readCacheEntry(authUserCacheKey);
            const authCacheIsValid = Boolean(authUserCacheEntry?.value) && getRemainingMs(authUserCacheEntry.cachedAt, AUTH_USER_TTL_MS) > 0;
            let authFlow;
            if (authCacheIsValid) {
                const userData = authUserCacheEntry.value;
                userData?.locale && i18n.changeLanguage(userData.locale);
                setUser(userData);
                hydrateDataProviderFromCache('auth/token', userData);
                scheduleRefresh(refreshAuthUser, getRemainingMs(authUserCacheEntry.cachedAt, AUTH_USER_TTL_MS));
                setAuthorizedState();
                authFlow = Promise.resolve(true);
            } else {
                removeCache(authUserCacheKey);
                authFlow = refreshAuthUser();
            }

            const userConfigCacheKey = getUserConfigCacheKey(hashToken(token));
            const refreshUserConfiguration = () => {
                return loadConfig(localStorage.getItem('token')).then((response) => {
                    if (response && response.item && response.item.data) {
                        writeCache(userConfigCacheKey, response.item.data);
                        if (!isCancelled) {
                            setUserCapabilities(response.item.data.capabilities ?? []);
                            setExistingModules(response.item.data.modules ?? {});
                        }
                    }
                    return true;
                }).catch((error) => {
                    const errorMessage = normalizeAuthErrorMessage(error);
                    setUnauthorizedState(errorMessage);
                    return false;
                });
            };

            authFlow.then((isAuthorized) => {
                if (!isAuthorized || isCancelled) return;
                const userConfigCacheEntry = readCacheEntry(userConfigCacheKey);
                const userConfigCacheIsValid = isValidUserConfiguration(userConfigCacheEntry?.value) && getRemainingMs(userConfigCacheEntry.cachedAt, USER_CONFIG_TTL_MS) > 0;
                if (userConfigCacheIsValid) {
                    const cachedUserConfiguration = userConfigCacheEntry.value;
                    setUserCapabilities(cachedUserConfiguration.capabilities ?? []);
                    setExistingModules(cachedUserConfiguration.modules ?? {});
                    hydrateDataProviderFromCache('configs/config', cachedUserConfiguration);
                    scheduleRefresh(refreshUserConfiguration, getRemainingMs(userConfigCacheEntry.cachedAt, USER_CONFIG_TTL_MS));
                } else {
                    removeCache(userConfigCacheKey);
                    refreshUserConfiguration();
                }
            });
        } else {
            setUserCapabilities([]);
            setExistingModules({});
            setUser(null);
            setLoginError(null);
            setLoginOptions(null);
            setAuthBootstrapStatus('anonymous');
        }

        return () => {
            isCancelled = true;
            timers.forEach((timerId) => window.clearTimeout(timerId));
        };
    }, [token])


    const concreteconfig = useMemo(
        () => ({
            appConfiguration,
            existingModules,
            innerHeight,
            innerWidth,
            user,
            setUser,
            loginError,
            setLoginError,
            loginOptions,
            setLoginOptions,
            userCapabilities,
            setUserCapabilities,
        }),
        [
            appConfiguration,
            existingModules,
            innerHeight,
            innerWidth,
            user,
            setUser,
            loginError,
            setLoginError,
            loginOptions,
            setLoginOptions,
            userCapabilities,
            setUserCapabilities,
        ],
    )

    return (
        (appConfiguration && authBootstrapStatus !== 'loading' && typeof userCapabilities !== 'undefined' && existingModules) ? (
            <AppContextProvider concreteconfig={concreteconfig}>
                <ContextualizedApp />
            </AppContextProvider>
        ) : (
            <Backdrop open={true} sx={{ display: 'flex', flexDirection: 'column' }}>
                <CircularProgress />
                <Box sx={{ fontSize: '14px', margin: '30px 0', zIndex: '10000', color: 'white', display: 'inline-flex', borderRadius: '10px' }}>Loading Configuration</Box>
            </Backdrop>
        )
    )
}

export default App;